import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit2, Lock, Eye, EyeOff, Check, X,
  CreditCard, Sparkles, Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import { getCurrentUser } from "../apis/auth";
import { getSubscriptionHistory, getUserCredits } from "../apis/subscription";
import SubscriptionCard from "../components/SubscriptionCard";
import CreditPurchaseModal from "../components/CreditPurchaseModal";
import { getApiBaseUrl } from "../config/apiBase";

const API_BASE = getApiBaseUrl();

const Profile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);

  const loadSubscriptionHistory = async () => {
    try {
      setSubscriptionsLoading(true);
      const response = await getSubscriptionHistory();
      if (response && response.data && response.data.success) {
        setCurrentSubscription(response.data.data.current_subscription);
        const sortedSubscriptions = response.data.data.history.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setSubscriptions(sortedSubscriptions);
      }
    } catch (error) {
      console.error("Error fetching subscription history:", error);
      toast.error("Failed to load subscription history");
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const loadUserCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await getUserCredits();
      if (response && response.data && response.data.success) {
        setUserCredits(response.data.data);
      }
    } catch (error) {
      console.error("Error loading user credits:", error);
    } finally {
      setCreditsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initProfile = async () => {
      let userFromStorage = null;
      try {
        const raw = localStorage.getItem("user");
        if (raw) userFromStorage = JSON.parse(raw);
      } catch (e) {
        // Invalid JSON in localStorage - will fetch from API
      }

      if (userFromStorage && !cancelled) {
        setUserData(userFromStorage);
        setForm({
          name: userFromStorage.name || "",
          email: userFromStorage.email || "",
          phone: userFromStorage.phone || "",
        });
      }

      loadSubscriptionHistory();
      loadUserCredits();

      if (!userFromStorage) {
        try {
          const response = await getCurrentUser();
          if (cancelled) return;
          if (response?.data?.success && response.data.user) {
            const u = response.data.user;
            setUserData(u);
            setForm({
              name: u.name || "",
              email: u.email || "",
              phone: u.phone || "",
            });
            localStorage.setItem("user", JSON.stringify(u));
          } else {
            setProfileLoadFailed(true);
          }
        } catch (err) {
          if (cancelled) return;
          setProfileLoadFailed(true);
          if (err.response?.status === 401) {
            localStorage.removeItem("user");
            localStorage.removeItem("userAccessToken");
            localStorage.removeItem("token");
            window.location.href = "/login";
            return;
          }
        }
      }
    };

    initProfile();
    return () => { cancelled = true; };
  }, []);

  const hasPassword = userData?.hasPassword ?? true;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    if (passwordError) setPasswordError("");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const updateUserDetails = async () => {
    setStatusMessage("");
    setIsLoading(true);
    try {
      const token = await localStorage.getItem("userAccessToken");
      const res = await fetch(`${API_BASE}/api/auth/updateUser`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to update");
      setUserData(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      setIsEditing(false);
      setStatusMessage("Profile updated successfully.");
      toast.success("Profile updated successfully!");
      setTimeout(() => { window.location.reload(); }, 1500);
    } catch (err) {
      setStatusMessage(err.message);
      toast.error(err.message || "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async () => {
    setPasswordError("");
    if (!passwordForm.newPassword) {
      setPasswordError("New password is required");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long");
      return;
    }
    if (hasPassword && !passwordForm.oldPassword) {
      setPasswordError("Current password is required");
      return;
    }
    setIsPasswordLoading(true);
    try {
      const token = localStorage.getItem("userAccessToken");
      const requestBody = hasPassword
        ? { oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword }
        : { newPassword: passwordForm.newPassword };
      const res = await fetch(`${API_BASE}/api/auth/updatePassword`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to update password");
      setShowPasswordModal(false);
      setPasswordForm({ oldPassword: "", newPassword: "" });
      const successMessage = hasPassword
        ? "Password changed successfully! You will be logged out for security."
        : "Password added successfully! You will be logged out for security.";
      toast.success(successMessage);
      if (!hasPassword && userData) {
        const updatedUser = { ...userData, hasPassword: true };
        setUserData(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
      setTimeout(() => { handleLogout(); }, 2000);
    } catch (err) {
      setPasswordError(err.message);
      toast.error(err.message || "Failed to update password");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setForm({
      name: userData.name,
      email: userData.email,
      phone: userData.phone,
    });
    setStatusMessage("");
  };

  const memberSince = userData?.created_at
    ? new Date(userData.created_at).toLocaleDateString("en-IN", {
        year: "numeric", month: "short", day: "numeric",
      })
    : "—";

  if (!userData) {
    if (profileLoadFailed) {
      return (
        <div className="prof-page">
          <div className="prof-empty">
            <p>Unable to load profile.</p>
            <button type="button" className="ping-btn ping-btn-green ping-btn-sm" onClick={() => { window.location.href = "/login"; }}>
              Go to Login
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="prof-page">
        <div className="prof-empty"><Loader2 size={22} className="animate-spin" /></div>
      </div>
    );
  }

  const replies = userCredits?.totalAvailableReplies || 0;
  const credits = userCredits?.totalCredits || 0;
  const perCredit = userCredits?.batches?.[0]?.creditPlan?.per_credit_ai_replies;

  return (
    <div className="prof-page">
      <div className="page-head">
        <div>
          <h2>{userData.name}</h2>
          <p>{userData.email} · Member since {memberSince}</p>
        </div>
        <div className="grow" />
        <span className="ping-chip g">Active</span>
        <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => setShowPasswordModal(true)}>
          <Lock size={14} />
          {hasPassword ? "Password" : "Add password"}
        </button>
        <button type="button" className="ping-btn ping-btn-green ping-btn-sm" onClick={() => navigate("/subscribe")}>
          <CreditCard size={14} />
          {currentSubscription ? "Upgrade / renew" : "Subscribe"}
        </button>
      </div>

      {statusMessage && (
        <div className={`ping-banner ${statusMessage.includes("successfully") ? "ok" : "red"}`}>
          {statusMessage}
        </div>
      )}

      <div className="fl-kpis">
        <div className="fl-kpi hi">
          <div>
            <div className="k">AI replies left</div>
            <div className="v">{creditsLoading ? "…" : replies.toLocaleString("en-IN")}</div>
          </div>
          <div className="ico"><Sparkles size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Credits</div>
            <div className="v">{creditsLoading ? "…" : credits.toLocaleString("en-IN")}</div>
          </div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Plans</div>
            <div className="v">{subscriptionsLoading ? "…" : subscriptions.length}</div>
          </div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Phone</div>
            <div className="v" style={{ fontSize: 15 }}>{userData.phone || "—"}</div>
          </div>
        </div>
      </div>

      <div className="prof-grid">
        <section className="prof-card">
          <div className="prof-card-head">
            <h3>Account</h3>
            {!isEditing && (
              <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => setIsEditing(true)}>
                <Edit2 size={13} /> Edit
              </button>
            )}
          </div>
          {isEditing ? (
            <form className="prof-form" onSubmit={(e) => { e.preventDefault(); updateUserDetails(); }}>
              <label>
                Full name
                <input name="name" value={form.name} onChange={handleChange} />
              </label>
              <label>
                Email
                <input name="email" type="email" value={form.email} onChange={handleChange} />
              </label>
              <label>
                Phone
                <input name="phone" value={form.phone} onChange={handleChange} />
              </label>
              <div className="prof-actions">
                <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={cancelEdit}>Cancel</button>
                <button type="submit" className="ping-btn ping-btn-green ping-btn-sm" disabled={isLoading}>
                  {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Save
                </button>
              </div>
            </form>
          ) : (
            <dl className="prof-dl">
              <div><dt>Name</dt><dd>{userData.name}</dd></div>
              <div><dt>Email</dt><dd>{userData.email}</dd></div>
              <div><dt>Phone</dt><dd>{userData.phone || "—"}</dd></div>
              <div><dt>Member since</dt><dd>{memberSince}</dd></div>
            </dl>
          )}
        </section>

        <section className="prof-card">
          <div className="prof-card-head">
            <h3>AI credits</h3>
            <button type="button" className="ping-btn ping-btn-green ping-btn-sm" onClick={() => setShowCreditModal(true)}>
              <Sparkles size={13} /> Buy credits
            </button>
          </div>
          {creditsLoading ? (
            <div className="prof-empty"><Loader2 size={18} className="animate-spin" /></div>
          ) : credits > 0 ? (
            <>
              {perCredit && (
                <p className="prof-hint">{credits} credits · 1 credit = {perCredit} replies</p>
              )}
              <ul className="prof-batches">
                {(userCredits.batches || []).map((batch, index) => (
                  <li key={batch.id || index}>
                    <span>{batch.creditPlan?.plan_name || "Credit plan"}</span>
                    <b>{batch.available_credits} cr</b>
                    <em>
                      {batch.expires_at
                        ? `Exp ${new Date(batch.expires_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}`
                        : "Lifetime"}
                    </em>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="prof-empty">
              <p>No credits yet. Buy some to use the AI chatbot.</p>
            </div>
          )}
        </section>
      </div>

      <section className="prof-card">
        <div className="prof-card-head">
          <h3>Subscriptions</h3>
          <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => navigate("/subscribe")}>
            <CreditCard size={13} />
            {currentSubscription ? "Upgrade / renew" : "Subscribe"}
          </button>
        </div>
        {subscriptionsLoading ? (
          <div className="prof-empty"><Loader2 size={18} className="animate-spin" /></div>
        ) : subscriptions.length > 0 ? (
          <div className="prof-subs">
            {subscriptions.map((subscription, index) => (
              <SubscriptionCard key={subscription.id} subscription={subscription} />
            ))}
          </div>
        ) : (
          <div className="prof-empty">
            <p>No subscriptions yet.</p>
          </div>
        )}
      </section>

      {showPasswordModal && (
        <>
          <div className="ping-drawer-backdrop" onClick={() => { setShowPasswordModal(false); setPasswordError(""); }} />
          <aside className="ping-drawer" role="dialog" aria-modal="true">
            <div className="ping-drawer-head">
              <div>
                <h3>{hasPassword ? "Change password" : "Add password"}</h3>
                <p>You’ll be signed out after this</p>
              </div>
              <button type="button" className="camp-ico" onClick={() => { setShowPasswordModal(false); setPasswordError(""); }} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="ping-drawer-body">
              {hasPassword && (
                <label>
                  Current password
                  <span className="prof-pw">
                    <input
                      type={showOldPassword ? "text" : "password"}
                      name="oldPassword"
                      value={passwordForm.oldPassword}
                      onChange={handlePasswordChange}
                      placeholder="Current password"
                    />
                    <button type="button" onClick={() => setShowOldPassword((v) => !v)} aria-label="Toggle visibility">
                      {showOldPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </span>
                </label>
              )}
              <label>
                {hasPassword ? "New password" : "Password"}
                <span className="prof-pw">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Min 6 characters"
                  />
                  <button type="button" onClick={() => setShowNewPassword((v) => !v)} aria-label="Toggle visibility">
                    {showNewPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </span>
              </label>
              {passwordError && <div className="ping-banner red">{passwordError}</div>}
            </div>
            <div className="ping-drawer-foot">
              <button type="button" className="ping-btn ping-btn-ghost" onClick={() => { setShowPasswordModal(false); setPasswordForm({ oldPassword: "", newPassword: "" }); setPasswordError(""); }}>
                Cancel
              </button>
              <button type="button" className="ping-btn ping-btn-green" disabled={isPasswordLoading} onClick={changePassword}>
                {isPasswordLoading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                {hasPassword ? "Update" : "Add password"}
              </button>
            </div>
          </aside>
        </>
      )}

      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSuccess={() => {
          loadUserCredits();
          loadSubscriptionHistory();
        }}
      />
    </div>
  );
};

export default Profile;
